// Minimal `fetch` wrappers for the fire-and-forget POSTs that used to go through `up.request`.
//
// They exist for the same reason as util/events: code shared between the Unpoly app and the Inertia
// app must not need the `up` global. Unpoly added the CSRF token automatically (see
// `up.protocol.config.csrfToken` in boot/custom_unpoly), so we do the same from the `CSRF-TOKEN`
// cookie ApplicationController sets.

import { getCsrfToken } from './csrf'

function headers(contentType?: string): HeadersInit {
  const result: Record<string, string> = {
    'X-Requested-With': 'XMLHttpRequest',
  }
  if (contentType) {
    result['Content-Type'] = contentType
  }
  const csrfToken = getCsrfToken()
  if (csrfToken) {
    result['X-CSRF-Token'] = csrfToken
  }
  return result
}

// Params are encoded the way `up.request({ params })` encoded them: `application/x-www-form-urlencoded`,
// with array values repeated under the same key (matching Rails' `foo[]` convention).
export async function postForm(
  url: string,
  params: Record<string, unknown>,
  options: RequestOptions = {},
): Promise<Response | undefined> {
  return send('POST', url, formBody(params), FORM_CONTENT_TYPE, options)
}

export async function patchForm(
  url: string,
  params: Record<string, unknown>,
  options: RequestOptions = {},
): Promise<Response | undefined> {
  return send('PATCH', url, formBody(params), FORM_CONTENT_TYPE, options)
}

const FORM_CONTENT_TYPE = 'application/x-www-form-urlencoded; charset=UTF-8'

function formBody(params: Record<string, unknown>): string {
  const body = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const entry of value) {
        body.append(key, String(entry))
      }
    } else if (value !== null && value !== undefined) {
      body.append(key, String(value))
    }
  }
  return body.toString()
}

// For requests that carry a file. The browser picks the Content-Type itself (multipart with boundary),
// which is why this hands the FormData straight to fetch instead of going through `send`.
export async function postMultipart(url: string, form: FormData): Promise<Response | undefined> {
  try {
    return await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: headers(),
      body: form,
    })
  } catch (error) {
    console.error(`Request to ${url} failed`, error)
  }
}

export async function getJson<T>(url: string): Promise<T | undefined> {
  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    })
    if (!response.ok) return

    return (await response.json()) as T
  } catch (error) {
    console.error(`Request to ${url} failed`, error)
  }
}

export interface RequestOptions {
  /**
   * `'manual'` leaves a redirect unfollowed (the response is then an `opaqueredirect`), for when rendering its
   * target would use up the flash meant for the next page load.
   */
  redirect?: RequestRedirect
}

export interface JsonRequestOptions extends RequestOptions {
  /**
   * Let the request outlive the page, for state flushes fired from `visibilitychange` — a normal fetch
   * would be cancelled as the tab goes away. Preferred over `navigator.sendBeacon` because it still allows
   * the `X-CSRF-Token` header. The body limit is 64 KB.
   */
  keepalive?: boolean
}

export async function postJson(
  url: string,
  payload: unknown,
  options: JsonRequestOptions = {},
): Promise<Response | undefined> {
  return send('POST', url, JSON.stringify(payload), 'application/json', options)
}

export async function deleteJson(url: string, payload: unknown): Promise<Response | undefined> {
  return send('DELETE', url, JSON.stringify(payload), 'application/json')
}

async function send(
  method: 'POST' | 'PATCH' | 'DELETE',
  url: string,
  body: string,
  contentType: string,
  { keepalive = false, redirect = 'follow' }: JsonRequestOptions = {},
): Promise<Response | undefined> {
  try {
    return await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: headers(contentType),
      body,
      keepalive,
      redirect,
    })
  } catch (error) {
    // A network failure must never break playback, so callers get `undefined` rather than a rejection.
    // Callers that need the response body have to handle that.
    console.error(`Request to ${url} failed`, error)
  }
}

export async function deleteRequest(url: string): Promise<Response | undefined> {
  try {
    return await fetch(url, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: headers(),
    })
  } catch (error) {
    console.error(`Request to ${url} failed`, error)
  }
}
